import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
} from '@mui/material'
import type { Theme } from '@mui/material/styles'
import { RailContainer } from './sidebar.styles'
import {
  mainSidebars,
  moreSidebars,
  payablesSidebars,
  receivablesSidebars,
} from './sidebar.constant'
import { Link, useLocation } from 'react-router-dom'
import SidebarButtons from '../sidebar-buttons/sidebar-buttons.component'
import { IconChevronDown, IconChevronUp, IconGridMenu, SidebarNavIcon } from './sidebar-nav-icons'
import { useGetUserCompanyAssociationByUserQuery } from 'store/company/company.api'
import { getUserId } from 'utils/common'

type SidebarProps = {
  close?: () => void
  /** Full-width rail inside mobile drawer */
  inDrawer?: boolean
  /** Fill drawer viewport height */
  fullViewportHeight?: boolean
}

const navItemSx = (theme: Theme, nested?: boolean) => ({
  minHeight: nested ? 44 : 48,
  // Nest children under expandable groups (Receivables / Payables / More)
  pl: nested ? 4.5 : 1.25,
  pr: 1.25,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  color: 'rgba(255,255,255,0.92)',
  textDecoration: 'none',
  borderRadius: 0,
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderLeft: `3px solid ${theme.palette.common.white}`,
    '&:hover': { backgroundColor: theme.palette.primary.dark },
  },
  '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
})

const toPath = (href: string) => (href.startsWith('/') ? href : `/${href}`)

const Sidebar = ({ close, inDrawer = false, fullViewportHeight = false }: SidebarProps) => {
  const { pathname } = useLocation()
  const [isReceivablesExpanded, setIsReceivablesExpanded] = useState(false)
  const [isPayablesExpanded, setIsPayablesExpanded] = useState(false)
  const [isMoreExpanded, setIsMoreExpanded] = useState(false)

  const userId = getUserId()
  const { data: userCompanyAssociations, isSuccess: associationsLoaded } =
    useGetUserCompanyAssociationByUserQuery({ userId }, { skip: !userId })
  const showFactoringNav = useMemo(
    () =>
      associationsLoaded && Boolean(userCompanyAssociations?.some((a) => a.factoringEnabled)),
    [associationsLoaded, userCompanyAssociations]
  )

  const isItemSelected = (href: string) => {
    if (href === 'trip' && pathname.startsWith('/trip/create')) {
      return false
    }
    if (href === 'trip' && pathname.startsWith('/carrier-trip')) {
      return false
    }
    // /carrier payables must not highlight when on /carrier-trip
    if (href === 'carrier') {
      return pathname === '/carrier' || pathname.startsWith('/carrier/')
    }
    if (href === 'carrier-trip') {
      return pathname.startsWith('/carrier-trip')
    }
    // Payables > Driver must not highlight More > Driver (/driver)
    if (href === 'driver-payables') {
      return (
        pathname === '/driver-payables' ||
        pathname.startsWith('/driver-payables/')
      )
    }
    if (href === 'driver') {
      return (
        (pathname === '/driver' || pathname.startsWith('/driver/')) &&
        !pathname.startsWith('/driver-payables')
      )
    }
    return pathname !== '/' && href !== '/'
      ? pathname.includes(href)
      : pathname.endsWith(href)
  }

  const isAnyReceivablesItemSelected = receivablesSidebars.some((sidebar) =>
    isItemSelected(sidebar.href)
  )
  const isAnyPayablesItemSelected = payablesSidebars.some((sidebar) =>
    isItemSelected(sidebar.href)
  )
  const isAnyMoreItemSelected = moreSidebars.some((sidebar) => isItemSelected(sidebar.href))

  useEffect(() => {
    if (isAnyReceivablesItemSelected) {
      setIsReceivablesExpanded(true)
    }
  }, [isAnyReceivablesItemSelected])

  useEffect(() => {
    if (isAnyPayablesItemSelected) {
      setIsPayablesExpanded(true)
    }
  }, [isAnyPayablesItemSelected])

  useEffect(() => {
    if (isAnyMoreItemSelected) {
      setIsMoreExpanded(true)
    }
  }, [isAnyMoreItemSelected])

  const handleReceivablesToggle = () => setIsReceivablesExpanded((prev) => !prev)
  const handlePayablesToggle = () => setIsPayablesExpanded((prev) => !prev)
  const handleMoreToggle = () => setIsMoreExpanded((prev) => !prev)

  const role = window.sessionStorage.getItem('role') ?? 'SUPER_ADMIN'
  const filterNav = <T extends { allowedRoles?: string[]; requiresFactoringCompany?: boolean }>(
    item: T
  ) => {
    if (item.allowedRoles && !item.allowedRoles.includes(role as 'SUPER_ADMIN' | 'DISPATCH')) {
      return false
    }
    if (item.requiresFactoringCompany && !showFactoringNav) {
      return false
    }
    return true
  }

  const visibleReceivablesSidebars = receivablesSidebars.filter(filterNav)
  const visiblePayablesSidebars = payablesSidebars.filter(filterNav)

  const onNavClick = () => {
    close?.()
  }

  return (
    <RailContainer $inDrawer={inDrawer} $fullHeight={fullViewportHeight}>
      <List
        component="nav"
        disablePadding
        sx={{
          flex: '1 1 0',
          minHeight: 0,
          overflowX: 'hidden',
          overflowY: 'auto',
          py: 0.5,
        }}
      >
        {mainSidebars.filter(filterNav).map((sidebar) => (
          <ListItemButton
            key={sidebar.label}
            component={Link}
            to={toPath(sidebar.href)}
            onClick={onNavClick}
            selected={isItemSelected(sidebar.href)}
            sx={(theme) => navItemSx(theme)}
          >
            <ListItemIcon
              sx={{
                minWidth: 40,
                justifyContent: 'center',
                color: 'inherit',
              }}
            >
              <SidebarNavIcon href={sidebar.href} size={22} />
            </ListItemIcon>
            <ListItemText
              primary={sidebar.label}
              primaryTypographyProps={{
                fontSize: 15,
                fontWeight: 600,
                noWrap: true,
              }}
            />
          </ListItemButton>
        ))}

        {visibleReceivablesSidebars.length > 0 && (
          <>
            <ListItemButton
              onClick={handleReceivablesToggle}
              selected={isAnyReceivablesItemSelected}
              sx={(theme) => navItemSx(theme)}
            >
              <ListItemIcon
                sx={{
                  minWidth: 40,
                  justifyContent: 'center',
                  color: 'inherit',
                }}
              >
                <SidebarNavIcon href="receivables" size={22} />
              </ListItemIcon>
              <ListItemText
                primary="Receivables"
                primaryTypographyProps={{
                  fontSize: 15,
                  fontWeight: 600,
                }}
                sx={{ flex: '1 1 auto', mr: 0.5, minWidth: 0 }}
              />
              <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                {isReceivablesExpanded ? (
                  <IconChevronUp size={20} />
                ) : (
                  <IconChevronDown size={20} />
                )}
              </ListItemIcon>
            </ListItemButton>

            <Collapse in={isReceivablesExpanded} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ pl: 1 }}>
                {visibleReceivablesSidebars.map((sidebar) => (
                  <ListItemButton
                    key={sidebar.label}
                    component={Link}
                    to={toPath(sidebar.href)}
                    onClick={onNavClick}
                    selected={isItemSelected(sidebar.href)}
                    sx={(theme) => navItemSx(theme, true)}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 40,
                        justifyContent: 'center',
                        color: 'inherit',
                      }}
                    >
                      <SidebarNavIcon href={sidebar.href} size={20} />
                    </ListItemIcon>
                    <ListItemText
                      primary={sidebar.label}
                      primaryTypographyProps={{
                        fontSize: 14,
                        fontWeight: 600,
                        noWrap: true,
                      }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          </>
        )}

        {visiblePayablesSidebars.length > 0 && (
          <>
            <ListItemButton
              onClick={handlePayablesToggle}
              selected={isAnyPayablesItemSelected}
              sx={(theme) => navItemSx(theme)}
            >
              <ListItemIcon
                sx={{
                  minWidth: 40,
                  justifyContent: 'center',
                  color: 'inherit',
                }}
              >
                <SidebarNavIcon href="payables" size={22} />
              </ListItemIcon>
              <ListItemText
                primary="Payables"
                primaryTypographyProps={{
                  fontSize: 15,
                  fontWeight: 600,
                }}
                sx={{ flex: '1 1 auto', mr: 0.5, minWidth: 0 }}
              />
              <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                {isPayablesExpanded ? <IconChevronUp size={20} /> : <IconChevronDown size={20} />}
              </ListItemIcon>
            </ListItemButton>

            <Collapse in={isPayablesExpanded} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ pl: 1 }}>
                {visiblePayablesSidebars.map((sidebar) => (
                  <ListItemButton
                    key={sidebar.label}
                    component={Link}
                    to={toPath(sidebar.href)}
                    onClick={onNavClick}
                    selected={isItemSelected(sidebar.href)}
                    sx={(theme) => navItemSx(theme, true)}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 40,
                        justifyContent: 'center',
                        color: 'inherit',
                      }}
                    >
                      <SidebarNavIcon href={sidebar.href} size={20} />
                    </ListItemIcon>
                    <ListItemText
                      primary={sidebar.label}
                      primaryTypographyProps={{
                        fontSize: 14,
                        fontWeight: 600,
                        noWrap: true,
                      }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          </>
        )}

        <ListItemButton
          onClick={handleMoreToggle}
          selected={isAnyMoreItemSelected}
          sx={(theme) => navItemSx(theme)}
        >
          <ListItemIcon
            sx={{
              minWidth: 40,
              justifyContent: 'center',
              color: 'inherit',
            }}
          >
            <IconGridMenu size={22} />
          </ListItemIcon>
          <ListItemText
            primary={isMoreExpanded ? 'Less' : 'More'}
            primaryTypographyProps={{
              fontSize: 15,
              fontWeight: 600,
            }}
            sx={{ flex: '1 1 auto', mr: 0.5, minWidth: 0 }}
          />
          <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
            {isMoreExpanded ? <IconChevronUp size={20} /> : <IconChevronDown size={20} />}
          </ListItemIcon>
        </ListItemButton>

        <Collapse in={isMoreExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {moreSidebars.filter(filterNav).map((sidebar) => (
              <ListItemButton
                key={sidebar.label}
                component={Link}
                to={toPath(sidebar.href)}
                onClick={onNavClick}
                selected={isItemSelected(sidebar.href)}
                sx={(theme) => navItemSx(theme)}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    justifyContent: 'center',
                    color: 'inherit',
                  }}
                >
                  <SidebarNavIcon href={sidebar.href} size={22} />
                </ListItemIcon>
                <ListItemText
                  primary={sidebar.label}
                  primaryTypographyProps={{
                    fontSize: 15,
                    fontWeight: 600,
                    noWrap: true,
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Collapse>
      </List>

      <Box sx={{ flexShrink: 0 }}>
        <SidebarButtons close={close} />
      </Box>
    </RailContainer>
  )
}

export default Sidebar
